import { createContext, useContext } from "react";

/**
 * The single seam every component reads through -- see hooks/useAuth.js. Populated by exactly
 * one of Auth0AuthBridge (today's default) or GatewayAuthBridge (border-gateway migration,
 * AUTH_MODE=gateway -- see App.js), never both, so this file itself never branches and calls
 * exactly one hook (useContext), which is what keeps it lint-clean under
 * react-hooks/rules-of-hooks: the mode decision happens once, in App.js's plain conditional
 * RENDER, not as a conditional hook call.
 *
 * Shape: { isAuthenticated, isLoading, user, error, logout, loginWithRedirect,
 * getAccessTokenSilently } -- exactly what @auth0/auth0-react's useAuth0() already returns,
 * because every existing consumer (AppContent, SecureRoute, Login, Logout,
 * useAuth0ApiReady, AppShell, ExplorerAppHeader) was written against that shape. Gateway mode
 * adds `expiresAt` (epoch ms of the hard session expiry); auth0-spa mode leaves it undefined.
 */
export const AuthContext = createContext(null);

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error(
      "useAuth() was called outside of Auth0AuthBridge / GatewayAuthBridge -- check that App.js still wraps the tree in one of them."
    );
  }
  return ctx;
}
